import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { Button } from "@/design-system/components/Button";
import { Input } from "@/design-system/components/Input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/design-system/components/Card";
import { Label } from "@/design-system/components/Label";
import { Lock, ArrowRight, Loader2, AlertTriangle, Building2, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { getRoleDashboard, isSuperAdmin } from "@shared/permissions";
import { AuthLogo } from "@/components/AuthLogo";

// Pack 11.A — WorkOS Consolidation (Task #1539). The "Continue with SSO"
// button is conditional on a tenant-specific probe: as the user types
// their email we ask `/api/auth/sso/status?email=` whether the email's
// domain belongs to a tenant whose Super Admin (or Tenant Admin holding
// `settings.manage`) has flipped the per-tenant `tenants.sso_enabled`
// toggle on. Until an `@` appears we don't probe; once it does we
// debounce by 250ms to avoid hammering the endpoint while the user is
// still typing the local-part of their address.
function useSsoEnabled(email: string): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (!email.includes("@")) {
      setEnabled(false);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      fetch(`/api/auth/sso/status?email=${encodeURIComponent(email.trim())}`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : { enabled: false }))
        .then((data) => {
          if (!cancelled) setEnabled(Boolean(data?.enabled));
        })
        .catch(() => {});
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [email]);
  return enabled;
}

const SSO_REASON_MESSAGES: Record<string, string> = {
  sso_invalid_callback: "The SSO callback was missing required parameters. Please try again.",
  sso_invalid_state: "The SSO sign-in could not be verified. Please try again.",
  sso_no_account: "Your SSO account isn't linked to a Nashid user. Contact your administrator.",
  sso_user_not_provisioned: "Your SSO account isn't linked to a Nashid user. Contact your administrator.",
  sso_account_deactivated: "Your account has been deactivated. Contact your administrator.",
  sso_missing_email: "Your identity provider didn't return an email address.",
  sso_profile_missing_email: "Your identity provider didn't return an email address.",
  sso_exchange_failed: "Could not complete sign-in with your identity provider. Please try again.",
  // Pack 11.A — Task #1539. Partner-portal accounts are excluded from
  // the enterprise SSO surface even when their email domain matches an
  // SSO-bound tenant; their canonical entry point is the partner login.
  sso_not_available_for_partner_account:
    "This account uses the partner login. Open the partner sign-in page and try again.",
  tenant_suspended: "Your tenant is suspended. Contact your administrator.",
};

async function getConfiguredLandingPage(role: string): Promise<string> {
  // First, try the profile-driven landing route (Task #2324 — Operational Profiles)
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch("/api/admin/landing-route", { credentials: "include", signal: controller.signal });
    clearTimeout(timeout);
    if (res.ok) {
      const data = await res.json();
      if (data?.data?.defaultLandingRoute && data.data.defaultLandingRoute !== "/") {
        return data.data.defaultLandingRoute;
      }
    }
  } catch {}
  // Fall back to navigation config, then role default
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(`/api/navigation-config/${encodeURIComponent(role)}`, { credentials: "include", signal: controller.signal });
    clearTimeout(timeout);
    if (res.ok) {
      const config = await res.json();
      if (config?.landingPage) return config.landingPage;
    }
  } catch {}
  return getRoleDashboard(role);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { login } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const sessionExpired = searchParams.get("reason") === "session_expired";
  const passwordChanged = searchParams.get("passwordChanged") === "1";
  const ssoEnabled = useSsoEnabled(email);
  const ssoErrorReason = searchParams.get("reason") ?? searchParams.get("error");
  const ssoErrorMessage = ssoErrorReason ? SSO_REASON_MESSAGES[ssoErrorReason] : null;

  useEffect(() => {
    if (passwordChanged) {
      toast({
        title: "Password updated",
        description: "Your new password has been set. Please log in to continue.",
      });
    }
  }, []);
  // GAP-025: honour a ?redirect= param so users who were sent here from a
  // protected URL land back on their intended page after authenticating.
  // Only relative paths are honoured to prevent open-redirect attacks.
  const redirectParam = searchParams.get("redirect");
  const safeRedirect =
    redirectParam &&
    redirectParam.startsWith("/") &&
    !redirectParam.startsWith("//")
      ? redirectParam
      : null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(email, password);
    setIsLoading(false);
    
    if (result.success) {
      if (result.mfaRequired) {
        setLocation("/auth/mfa-totp");
        return;
      }
      toast({
        title: "Welcome back",
        description: "Logged in successfully",
      });
      // GAP-025: if the user was sent here from a protected URL, send them back there.
      // Skip the redirect for super-admins and partner accounts which have fixed
      // landing pages that differ from the protected-URL namespace.
      if (safeRedirect && !isSuperAdmin(result.role) && !result.partnerPortalRedirect) {
        setLocation(safeRedirect);
      } else if (isSuperAdmin(result.role)) {
        setLocation("/super-admin/dashboard");
      } else if (result.partnerPortalRedirect) {
        if (result.partnerIsUnassigned) {
          setLocation("/partner/home");
        } else {
          setLocation("/partner/dashboard");
        }
      } else if (result.hasCompletedOnboarding === false) {
        setLocation("/onboarding/welcome");
      } else {
        const landingPage = await getConfiguredLandingPage(result.role || "");
        setLocation(landingPage);
      }
    } else if (result.requiresPasswordChange) {
      setLocation(`/auth/force-change-password?userId=${encodeURIComponent(result.userId || "")}`);
    } else {
      toast({
        variant: "destructive",
        title: "Invalid credentials",
        description: result.error || "Please enter a valid email and password.",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-5 dark:opacity-10 pointer-events-none" />
      <Card className="w-full max-w-md border-t-4 border-t-primary shadow-xl z-10">
        {sessionExpired && (
          <div className="flex items-center gap-2 px-6 pt-4 pb-2 text-sm text-amber-700 bg-amber-50 border-b border-amber-200 rounded-t-lg" data-testid="banner-session-expired">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            Your session has expired. Please sign in again.
          </div>
        )}
        {ssoErrorMessage && (
          <div className="flex items-center gap-2 px-6 pt-4 pb-2 text-sm text-rose-700 bg-rose-50 border-b border-rose-200 rounded-t-lg" data-testid="banner-sso-error">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {ssoErrorMessage}
          </div>
        )}
        <CardHeader className="space-y-1 text-center pb-8">
          <div className="flex justify-center mb-4">
            <AuthLogo className="h-12" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">Workforce Platform</CardTitle>
          <CardDescription>Please enter your login credentials to access the workspace</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="admin@tenant.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link href="/auth/forgot-password" className="text-xs text-primary hover:underline" data-testid="link-forgot-password">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="button-toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading} data-testid="button-login">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Authenticating...
                </>
              ) : (
                <>
                  Sign In <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
          {ssoEnabled && (
            <>
              <div className="relative my-6" data-testid="sso-divider">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200 dark:border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                data-testid="button-sso-login"
                onClick={() => {
                  // Carry the typed email so the server can resolve the
                  // tenant's `sso_connections` row by domain at /authorize.
                  const params = new URLSearchParams();
                  if (email) params.set("email", email.trim());
                  window.location.href = `/api/auth/sso/authorize${params.toString() ? `?${params.toString()}` : ""}`;
                }}
              >
                <Building2 className="mr-2 h-4 w-4" />
                Continue with Single Sign-On
              </Button>
            </>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4 border-t pt-6 bg-muted/20">
          <div className="text-xs text-center text-muted-foreground">
            By logging in, you agree to the{" "}
            <Link href="/terms" className="underline" data-testid="link-terms-of-service">Terms of Service</Link>
            {" "}and{" "}
            <Link href="/privacy" className="underline" data-testid="link-privacy-policy">Privacy Policy</Link>.
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/60">
            <Lock className="h-3 w-3" />
            Secured by Nashid Identity
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
